import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BENCHMARK_BY_KEY } from "@/lib/marketing/constants";
import { cn } from "@/lib/utils";

interface Benchmark {
  key: string;
  target: number;
  label: string;
  appliesTo: string;
  rationale: string;
}

// Group headings by key prefix, in display order.
const GROUPS: { title: string; match: (k: string) => boolean }[] = [
  {
    title: "Organic Social — Engagement & Growth",
    match: (k) =>
      k.startsWith("engagement_rate") ||
      k === "reach_growth_mom" ||
      k === "follower_growth_mom" ||
      k === "save_rate" ||
      k === "link_ctr",
  },
  { title: "FP Leads & Close Rates", match: (k) => k.startsWith("fp_ig") || k.startsWith("fp_tt") },
  {
    title: "Website & Email",
    match: (k) =>
      k.startsWith("web_") ||
      k.startsWith("quote_") ||
      k.startsWith("sotu_") ||
      k.startsWith("bounce_") ||
      k === "weekly_quote_submissions",
  },
  { title: "Paid Advertising", match: (k) => k.includes("paid") },
];

function unitOf(key: string): "ratio" | "count" | "currency" {
  return BENCHMARK_BY_KEY[key]?.unit ?? "count";
}

function BenchmarkRow({
  b,
  onSave,
}: {
  b: Benchmark;
  onSave: (key: string, target: number) => void;
}) {
  const unit = unitOf(b.key);
  const display = unit === "ratio" ? (b.target * 100).toString() : b.target.toString();
  const [saved, setSaved] = useState(false);

  const handleSave = async (raw: string) => {
    const n = Number(raw.trim());
    if (!Number.isFinite(n)) return;
    const target = unit === "ratio" ? n / 100 : n;
    if (target === b.target) return;
    const res = await fetch(`/api/marketing-suite/benchmarks/${b.key}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ target }),
    });
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
      onSave(b.key, target);
    }
  };

  return (
    <tr className="border-b last:border-0 align-top">
      <td className="py-2 pr-4">
        <div className="font-medium">{b.label}</div>
        <div className="text-xs text-muted-foreground">{b.rationale}</div>
      </td>
      <td className="py-2 pr-2 text-xs text-muted-foreground whitespace-nowrap">{b.appliesTo}</td>
      <td className="py-2 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1">
          {unit === "currency" && <span className="text-muted-foreground">$</span>}
          <input
            type="number"
            defaultValue={display}
            onBlur={(e) => handleSave(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            className={cn(
              "w-24 rounded border bg-background px-2 py-1 text-right tabular-nums outline-none focus:border-brand-gold",
              saved && "border-green-500 ring-1 ring-green-500/30",
            )}
          />
          {unit === "ratio" && <span className="text-muted-foreground">%</span>}
        </div>
      </td>
    </tr>
  );
}

export function BenchmarkSettings() {
  const [benchmarks, setBenchmarks] = useState<Benchmark[] | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    fetch("/api/marketing-suite/benchmarks", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setBenchmarks(d.benchmarks))
      .catch(() => setError(true));
  }, []);

  useEffect(() => load(), [load]);

  const handleSave = (key: string, target: number) => {
    setBenchmarks((prev) => prev?.map((b) => (b.key === key ? { ...b, target } : b)) ?? prev);
  };

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Couldn't load benchmarks.
        </CardContent>
      </Card>
    );
  }
  if (!benchmarks) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-6">
      {GROUPS.map((g) => {
        const items = benchmarks.filter((b) => g.match(b.key));
        if (items.length === 0) return null;
        return (
          <Card key={g.title}>
            <CardHeader>
              <CardTitle>{g.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left py-2 pr-4 font-medium">Metric</th>
                      <th className="text-left py-2 pr-2 font-medium">Applies To</th>
                      <th className="text-right py-2 font-medium">Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((b) => (
                      <BenchmarkRow key={b.key} b={b} onSave={handleSave} />
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
